import CountUp from 'react-countup';
import { AiOutlinePlus } from 'react-icons/ai';
import { Link, useNavigate } from 'react-router-dom';

import DarkModeToggle from '@/components/shared/DarkModeToggle';
import { TextToolTip } from '@/components/shared/TextToolTip';
import { Web3Wallet } from '@/components/shared/web3-wallet/web3-wallet';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { PATHS } from '@/constants/page-paths';
import { useAuth } from '@/lib/hooks/useAuth';
import { usePermission } from '@/lib/hooks/useRoutePermission';
import { useTwinVault } from '@/lib/hooks/useTwinVault';
import TwinCoin from '@/pages/profile/components/TwinCoin';
import { useExternalCurrencyStore, useSettingsStore } from '@/store';
import { ProfileDropdown } from '../../ProfileDropdown';
import { MobileMenu } from './MobileMenu';

export const RightNav = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { vaultData, isLoading } = useTwinVault();
  const { settings } = useSettingsStore();
  const checkPermission = usePermission();
  const checkPoints = checkPermission('buy_points');
  const checkProfile = checkPermission('profile');
  const { externalCurrencyEnabled } = useExternalCurrencyStore();

  const tokenEnabled = settings?.app?.isTokenEnabled ?? false;
  const userPays = settings?.suVariables?.userPays;

  return (
    <div className="flex items-center justify-between gap-2 lg:gap-4 h-full">
      {isAuthenticated ? (
        <>
          {/* Show points and buy button only if userPays is true */}
          {userPays && !externalCurrencyEnabled && (
            isLoading ? (
              <Skeleton className="w-20 h-6" />
            ) : (
              <div className="flex justify-center items-center px-2 py-1 gap-1 sm:gap-2 rounded-full bg-secondary text-secondary-foreground">
                {checkProfile && (
                  <Link to={`${PATHS.PROFILE}/?tab=vault`}>
                    <div className="flex gap-1 items-center sm:gap-2">
                      <TwinCoin />
                      <div className="font-semibold text-sm">
                        <CountUp
                          end={parseFloat(vaultData?.twinPoint) as unknown as number}
                          decimals={0}
                          duration={0.5}
                        />
                      </div>
                    </div>
                  </Link>
                )}

                {checkPoints && (
                  <TextToolTip label="Buy Points" side="bottom">
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(PATHS.BUY_POINTS);
                      }}
                      className="h-6 w-6 px-1 rounded-full"
                      data-testid="buy-point-button-right-nav"
                    >
                      <AiOutlinePlus />
                    </Button>
                  </TextToolTip>
                )}
              </div>
            )
          )}

          {externalCurrencyEnabled && vaultData?.externalCurrency && (
            <div className="flex justify-center items-center px-2 py-1 gap-1 sm:gap-2 rounded-full bg-secondary text-secondary-foreground">
              <TextToolTip label={vaultData?.externalCurrency?.currencyName} side="bottom">
                <div className="flex gap-1 items-center sm:gap-2">
                  {/* You can add a coin icon here if needed */}
                  <span className="bg-primary rounded-full text-primary-foreground w-6 h-6 p-3 flex items-center justify-center font-bold">
                    {vaultData.externalCurrency.currencyName.slice(0, 1).toUpperCase()}
                  </span>
                  <div className="font-semibold text-sm">
                    <CountUp
                      end={parseFloat(vaultData.externalCurrency.balance) as number}
                      decimals={1}
                      duration={0.5}
                    />
                  </div>
                </div>
              </TextToolTip>
            </div>
          )}

          {/* Show Web3 Wallet only if userPays is true */}
          {userPays && tokenEnabled && !externalCurrencyEnabled && (
            <div className="hidden sm:block">
              <Web3Wallet />
            </div>
          )}

          <ProfileDropdown />
          <MobileMenu />
        </>
      ) : (
        <div
          className="flex items-center gap-2 md:gap-4"
          data-testid="sign-in-right-nav"
        >
          <DarkModeToggle />
          <Link to={PATHS.SIGN_IN}>Sign-in</Link>
          <MobileMenu />
        </div>
      )}

    </div>
  );
};
